import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useGameLounge } from '../context/GameLoungeContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiShoppingCart, FiPlus, FiMinus, FiCheck, FiX, FiWifiOff } = FiIcons;

function MobileOrder() {
  const { stationId } = useParams();
  const { state, dispatch } = useGameLounge();
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [showCart, setShowCart] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Enhanced effect for detecting PWA mode and network status
  useEffect(() => {
    const isStandaloneMode = window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone ||
      document.referrer.includes('android-app://');
    
    setIsStandalone(isStandaloneMode);

    // Network status handling
    const handleOnline = () => {
      setIsOnline(true);
      // Reload page to ensure fresh data
      if (localStorage.getItem('offline-mode')) {
        window.location.reload();
        localStorage.removeItem('offline-mode');
      }
    };
    
    const handleOffline = () => {
      setIsOnline(false);
      localStorage.setItem('offline-mode', 'true');
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Add to homescreen for iOS (beforeinstallprompt doesn't work on iOS)
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isInstalled = localStorage.getItem('pwa-installed');
    
    if (isIOS && !isInstalled && !isStandaloneMode) {
      // Show iOS install hint after 2 seconds
      setTimeout(() => {
        alert('📱 Installiere diese App: Drücke das Teilen-Symbol (⬆️) und wähle "Zum Home-Bildschirm"');
        localStorage.setItem('pwa-installed', 'true');
      }, 2000);
    }

    // Prevent zoom on input focus (iOS)
    document.addEventListener('touchmove', function(event) {
      if (event.scale !== 1) {
        event.preventDefault();
      }
    }, { passive: false });

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const findStation = () => {
    for (const room of state.rooms) {
      const station = room.stations.find(s => s.id === stationId);
      if (station) {
        return { ...station, roomName: room.name };
      }
    }
    return null;
  };

  const station = findStation();

  const categories = ['Alle', ...new Set(state.kioskItems.map(item => item.category))];

  const filteredItems = selectedCategory === 'Alle'
    ? state.kioskItems
    : state.kioskItems.filter(item => item.category === selectedCategory);

  const addToCart = (item) => {
    const existingItem = cart.find(cartItem => cartItem.id === item.id);
    if (existingItem) {
      setCart(cart.map(cartItem =>
        cartItem.id === item.id
          ? { ...cartItem, quantity: cartItem.quantity + 1 }
          : cartItem
      ));
    } else {
      setCart([...cart, { ...item, quantity: 1 }]);
    }
  };

  const removeFromCart = (itemId) => {
    const existingItem = cart.find(cartItem => cartItem.id === itemId);
    if (existingItem && existingItem.quantity > 1) {
      setCart(cart.map(cartItem =>
        cartItem.id === itemId
          ? { ...cartItem, quantity: cartItem.quantity - 1 }
          : cartItem
      ));
    } else {
      setCart(cart.filter(cartItem => cartItem.id !== itemId));
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalItems = () => {
    return cart.reduce((sum, item) => sum + item.quantity, 0);
  };

  const submitOrder = () => {
    if (cart.length > 0) {
      // Check if online before submitting
      if (!navigator.onLine) {
        alert('⚠️ Du bist offline. Die Bestellung wird gesendet, sobald die Verbindung wiederhergestellt ist.');
        // Store order for later sync
        localStorage.setItem('pending-order', JSON.stringify({
          stationId,
          items: cart,
          timestamp: Date.now()
        }));
        setCart([]);
        setShowCart(false);
        setOrderSubmitted(true);
        setTimeout(() => setOrderSubmitted(false), 3000);
        return;
      }

      dispatch({
        type: 'ADD_ORDER',
        payload: {
          stationId,
          items: cart,
          total: getTotalPrice()
        }
      });
      setCart([]);
      setShowCart(false);
      setOrderSubmitted(true);
      setTimeout(() => setOrderSubmitted(false), 3000);
    }
  };

  // Sync pending orders when online
  useEffect(() => {
    const syncPendingOrders = () => {
      const pendingOrder = localStorage.getItem('pending-order');
      if (pendingOrder && navigator.onLine) {
        try {
          const orderData = JSON.parse(pendingOrder);
          dispatch({
            type: 'ADD_ORDER',
            payload: {
              stationId: orderData.stationId,
              items: orderData.items,
              total: orderData.items.reduce((total, item) => total + (item.price * item.quantity), 0)
            }
          });
          localStorage.removeItem('pending-order');
          alert('✅ Offline-Bestellung wurde erfolgreich synchronisiert!');
        } catch (error) {
          console.error('Error syncing pending order:', error);
          localStorage.removeItem('pending-order');
        }
      }
    };

    // Try to sync immediately when component mounts
    syncPendingOrders();

    // Set up interval to check for pending orders
    const interval = setInterval(syncPendingOrders, 5000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const getItemQuantity = (itemId) => {
    const cartItem = cart.find(item => item.id === itemId);
    return cartItem ? cartItem.quantity : 0;
  };

  // Show offline indicator
  const showOfflineIndicator = !isOnline;

  if (!station) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
        <div className="text-center bg-white rounded-lg p-8 shadow-lg">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Station nicht gefunden</h1>
          <p className="text-gray-600">Die angegebene Station existiert nicht.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {/* Offline Indicator */}
      {showOfflineIndicator && (
        <div className="fixed top-0 left-0 right-0 bg-red-500 text-white px-4 py-2 z-30 text-center flex items-center justify-center space-x-2">
          <SafeIcon icon={FiWifiOff} className="text-xl" />
          <span className="text-sm font-medium">Offline Modus - Bestellungen werden später gesendet</span>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg sticky top-0 z-20" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="px-4 py-4 safe-area-inset-top">
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h1 className="text-xl font-bold">Kiosk Bestellung</h1>
              <p className="text-sm text-blue-100">{station.roomName} - {station.name}</p>
            </div>
            <button
              onClick={() => setShowCart(!showCart)}
              className="relative bg-white bg-opacity-20 hover:bg-opacity-30 p-3 rounded-full transition-all active:scale-95"
            >
              <SafeIcon icon={FiShoppingCart} className="text-2xl" />
              {cart.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold shadow-lg">
                  {getTotalItems()}
                </span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 py-4 pb-24">
        {/* Success Message */}
        {orderSubmitted && (
          <div className="bg-green-500 text-white px-4 py-4 rounded-lg mb-4 flex items-center space-x-3 shadow-lg animate-bounce">
            <SafeIcon icon={FiCheck} className="text-2xl" />
            <span className="font-semibold">Bestellung erfolgreich aufgegeben!</span>
          </div>
        )}

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex space-x-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-6 py-3 rounded-full whitespace-nowrap transition-all font-medium shadow-md active:scale-95 ${
                  selectedCategory === category
                    ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white'
                    : 'bg-white text-gray-700 border border-gray-200'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Items Grid */}
        <div className="space-y-4 mb-6">
          {filteredItems.map(item => {
            const quantity = getItemQuantity(item.id);
            return (
              <div key={item.id} className="bg-white rounded-xl shadow-md overflow-hidden transform transition-all hover:shadow-lg">
                <div className="p-5">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900">{item.name}</h3>
                      <p className="text-sm text-gray-500 mb-2">{item.category}</p>
                      <p className="text-2xl font-bold text-green-600">
                        {item.price.toFixed(2)} {state.settings.currency}
                      </p>
                    </div>
                  </div>

                  {quantity === 0 ? (
                    <button
                      onClick={() => addToCart(item)}
                      className="w-full bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600 text-white px-6 py-4 rounded-lg font-semibold transition-all flex items-center justify-center space-x-2 shadow-md active:scale-95"
                    >
                      <SafeIcon icon={FiPlus} className="text-xl" />
                      <span>Hinzufügen</span>
                    </button>
                  ) : (
                    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-md active:scale-95"
                      >
                        <SafeIcon icon={FiMinus} className="text-xl" />
                      </button>
                      <span className="text-2xl font-bold text-gray-900">{quantity}</span>
                      <button
                        onClick={() => addToCart(item)}
                        className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-full flex items-center justify-center transition-all shadow-md active:scale-95"
                      >
                        <SafeIcon icon={FiPlus} className="text-xl" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredItems.length === 0 && (
          <div className="text-center py-16 bg-white rounded-xl shadow-md">
            <SafeIcon icon={FiShoppingCart} className="text-6xl text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500 text-lg">Keine Artikel in dieser Kategorie verfügbar.</p>
          </div>
        )}
      </div>

      {/* Floating Cart Button */}
      {cart.length > 0 && !showCart && (
        <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent" style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}>
          <button
            onClick={() => setShowCart(true)}
            className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-5 rounded-xl font-bold text-lg transition-all flex items-center justify-between shadow-2xl active:scale-95"
          >
            <span className="flex items-center space-x-2">
              <SafeIcon icon={FiShoppingCart} className="text-xl" />
              <span>{getTotalItems()} Artikel</span>
            </span>
            <span>{getTotalPrice().toFixed(2)} {state.settings.currency}</span>
          </button>
        </div>
      )}

      {/* Cart Modal */}
      {showCart && cart.length > 0 && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end" onClick={() => setShowCart(false)}>
          <div 
            className="bg-white w-full rounded-t-3xl shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
              <h3 className="text-xl font-bold text-gray-900">Warenkorb</h3>
              <button
                onClick={() => setShowCart(false)}
                className="text-gray-500 hover:text-gray-700 p-2"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {cart.map(item => (
                <div key={item.id} className="flex justify-between items-center bg-gray-50 p-4 rounded-lg">
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900">{item.name}</p>
                    <p className="text-sm text-gray-600">{item.quantity}x {item.price.toFixed(2)} {state.settings.currency}</p>
                  </div>
                  <p className="font-bold text-lg text-gray-900">
                    {(item.price * item.quantity).toFixed(2)} {state.settings.currency}
                  </p>
                </div>
              ))}
            </div>

            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg font-semibold text-gray-700">Gesamt:</span>
                <span className="text-2xl font-bold text-green-600">
                  {getTotalPrice().toFixed(2)} {state.settings.currency}
                </span>
              </div>

              <button
                onClick={submitOrder}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white px-6 py-5 rounded-xl font-bold text-lg transition-all flex items-center justify-center space-x-2 shadow-lg active:scale-95"
              >
                <SafeIcon icon={FiCheck} className="text-xl" />
                <span>Bestellung aufgeben</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MobileOrder;